import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { FixturesResponse } from "src/types/statsperform/fixtures";

import { SPORT_DATA, type SportDataStateType } from "./types";

const userInitialState: SportDataStateType = {
	tournamentCalendar: {
		data: null,
		loading: true,
		error: null,
	},
	standings: {
		data: {
			tournamentCalendarId: null,
			standingsData: null,
		},
		loading: false,
		error: null,
	},
	hockeyStandings: {
		data: {
			matchId: null,
			items: null,
		},
		loading: false,
		error: null,
	},
	schedule: {
		data: null,
		loading: true,
		error: null,
	},
	matchData: {
		data: {
			fixtureId: null,
			fixtureData: null,
		},
		loading: true,
		error: null,
	},
	fixtures: {
		data: null,
		loading: true,
		error: null,
	},
	lineup: {
		data: null,
		loading: true,
		error: null,
	},
	AskiDaLineup: {
		data: null,
		loading: true,
		error: null,
	},
};

export const sportDataSlice = createSlice({
	name: SPORT_DATA,
	initialState: userInitialState,
	reducers: {
		// Standings reducers
		requestStandings: (state: SportDataStateType) => {
			state.standings.loading = true;
			state.standings.error = null;
		},
		receiveStandings: (
			state: SportDataStateType,
			action: PayloadAction<SportDataStateType["standings"]["data"]>,
		) => {
			state.standings.data = action.payload;
			state.standings.loading = false;
			state.standings.error = null;
		},
		failedStandings: (state: SportDataStateType, action: PayloadAction<string>) => {
			state.standings.loading = false;
			state.standings.error = action.payload;
		},

		// Hockey Standings reducers
		requestHockeyStandings: (state: SportDataStateType) => {
			state.hockeyStandings.loading = true;
			state.hockeyStandings.error = null;
		},
		receiveHockeyStandings: (
			state: SportDataStateType,
			action: PayloadAction<SportDataStateType["hockeyStandings"]["data"]>,
		) => {
			state.hockeyStandings.data = action.payload;
			state.hockeyStandings.loading = false;
			state.hockeyStandings.error = null;
		},
		failedHockeyStandings: (state: SportDataStateType, action: PayloadAction<string>) => {
			state.hockeyStandings.loading = false;
			state.hockeyStandings.error = action.payload;
		},

		// Fixtures reducers
		requestFixtures: (state: SportDataStateType) => {
			state.fixtures.loading = true;
			state.fixtures.error = null;
		},
		receiveFixtures: (state: SportDataStateType, action: PayloadAction<FixturesResponse>) => {
			state.fixtures.data = action.payload;
			state.fixtures.loading = false;
			state.fixtures.error = null;
		},
		failedFixtures: (state: SportDataStateType, action: PayloadAction<string>) => {
			state.fixtures.loading = false;
			state.fixtures.error = action.payload;
		},

		// Schedule reducers
		requestSchedule: (state: SportDataStateType) => {
			state.schedule.loading = true;
			state.schedule.error = null;
		},
		receiveSchedule: (
			state: SportDataStateType,
			action: PayloadAction<SportDataStateType["schedule"]["data"]>,
		) => {
			state.schedule.data = action.payload;
			state.schedule.loading = false;
			state.schedule.error = null;
		},
		failedSchedule: (state: SportDataStateType, action: PayloadAction<string>) => {
			state.schedule.loading = false;
			state.schedule.error = action.payload;
		},

		// Match Data reducers
		requestMatchData: (state: SportDataStateType) => {
			state.matchData.loading = true;
			state.matchData.error = null;
		},
		receiveMatchData: (
			state: SportDataStateType,
			action: PayloadAction<SportDataStateType["matchData"]["data"]>,
		) => {
			state.matchData.data = action.payload;
			state.matchData.loading = false;
		},
		failedMatchData: (state: SportDataStateType, action: PayloadAction<string>) => {
			state.matchData.loading = false;
			state.matchData.error = action.payload;
		},

		// Tournament Calendar reducers
		requestTournamentCalendar: (state: SportDataStateType) => {
			state.tournamentCalendar.loading = true;
			state.tournamentCalendar.error = null;
		},
		receiveTournamentCalendar: (
			state: SportDataStateType,
			action: PayloadAction<SportDataStateType["tournamentCalendar"]["data"]>,
		) => {
			state.tournamentCalendar.data = action.payload;
			state.tournamentCalendar.loading = false;
			state.tournamentCalendar.error = null;
		},
		failedTournamentCalendar: (state: SportDataStateType, action: PayloadAction<string>) => {
			state.tournamentCalendar.loading = false;
			state.tournamentCalendar.error = action.payload;
		},

		// Lineup reducers
		requestLineup: (state: SportDataStateType) => {
			state.lineup.loading = true;
			state.lineup.error = null;
		},
		receiveLineup: (
			state: SportDataStateType,
			action: PayloadAction<SportDataStateType["lineup"]["data"]>,
		) => {
			state.lineup.data = action.payload;
			state.lineup.loading = false;
			state.lineup.error = null;
		},
		failedLineup: (state: SportDataStateType, action: PayloadAction<string>) => {
			state.lineup.loading = false;
			state.lineup.error = action.payload;
		},

		// AskiDaLineup reducers
		requestAskiDaLineup: (state: SportDataStateType) => {
			state.AskiDaLineup.loading = true;
			state.AskiDaLineup.error = null;
		},
		receiveAskiDaLineup: (
			state: SportDataStateType,
			action: PayloadAction<SportDataStateType["AskiDaLineup"]["data"]>,
		) => {
			state.AskiDaLineup.data = action.payload;
			state.AskiDaLineup.loading = false;
			state.AskiDaLineup.error = null;
		},
		failedAskiDaLineup: (state: SportDataStateType, action: PayloadAction<string>) => {
			state.AskiDaLineup.loading = false;
			state.AskiDaLineup.error = action.payload;
		},
	},
});

const actions = sportDataSlice.actions;
const sportDataReducer = sportDataSlice.reducer;

export { actions, sportDataReducer };
